import React, { useEffect, useState, useRef } from "react";
import {
  Col,
  Row,
  Space,
  Spin,
  Typography,
  Button,
  Table,
  Tag,
  message,
  Tooltip,
  DatePicker,
  Empty,
  Modal,
  Tabs,
  Statistic,
  Input,
  notification,
  Dropdown,
} from "antd";
import { Link, useParams, useNavigate } from "react-router-dom";
import { IntentGroup } from "../../model_gen/intent";
import { Config } from "../../model_gen/config";
import { usePermissions } from "../../PermissionsProvider";
import { IntentService } from "../../services/IntentService";
import { TestSetService } from "../../services/TestSetService";
import { TestSet } from "../../model_gen/evaluation";
import moment from "moment";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import {
  CopyOutlined,
  PlusOutlined,
  SettingOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  BellOutlined,
  HistoryOutlined,
  SearchOutlined,
  FileTextOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { ReactComponent as AiStars } from "../../assets/svg/ai-creation.svg";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { ColumnsType } from "antd/es/table";
import SentinelList from "../Sentinel/SentinelList";
import {
  PrimaryButton,
  OutlineButton,
  RequestViewButton,
} from "../shared/button/Buttons";
import IntentsTable from "../shared/IntentsTable";
import DatasetsTable from "../Finetune/Datasets/DatasetsTable";
import { DatasetService } from "../../services/DatasetService";
import { FinetuneDataset } from "../../model_gen/finetuning";
import ModelTable from "../shared/ModelTable";

dayjs.extend(utc);

const { TabPane } = Tabs;

const IntentGroupOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { intentGroupId } = useParams<{ intentGroupId: string }>();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [config, setConfig] = useState<Config>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsCurrentPage, setRequestsCurrentPage] = useState(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(10);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const requestsByPageRef = useRef<Record<number, any[]>>({});
  const [initialLoadDate] = useState<number>(Date.now());
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [intents, setIntents] = useState<any[]>([]);
  const [intentsLoading, setIntentsLoading] = useState<boolean>(false);
  const [intentsTotal, setIntentsTotal] = useState(0);
  const [intentsCurrentPage, setIntentsCurrentPage] = useState(1);
  const intentsByPageRef = useRef<Record<number, any[]>>({});
  const navigate = useNavigate();
  const [originalConfig, setOriginalConfig] = useState<Config>();
  const childRef = useRef(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [testSetsLoading, setTestSetsLoading] = useState<boolean>(false);
  const [testSetsCurrentPage, setTestSetsCurrentPage] = useState(1);
  const testSetsPageSize = 10;
  const [testSetsTotal, setTestSetsTotal] = useState<number>(0);
  const [cachedTestSets, setCachedTestSets] = useState<
    Record<number, TestSet[]>
  >({});
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState<boolean>(false);
  const [datasetsTotal, setDatasetsTotal] = useState<number>(0);
  const [datasetsCurrentPage, setDatasetsCurrentPage] = useState<number>(1);
  const datasetsPageSize = 10;

  const styles = {
    hoverRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsContainer: {
      borderRadius: "12px",
      border: "1px solid #f0f0f0",
      overflow: "hidden",
    },
    detailsHeader: {
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 24px",
      background: "#fafafa",
    },
    detailsContent: {
      padding: "24px",
    },
    tabIcon: {
      marginRight: "8px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    aiIconStyle: {
      width: "12px",
      height: "12px",
      color: "#8c8c8c",
      marginLeft: "4px",
      verticalAlign: "text-top",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
    actionButton: (hovered: boolean) => ({
      borderRadius: "6px",
      color: "#1890ff",
      display: "flex",
      alignItems: "center",
      padding: "0 10px",
      height: "32px",
      transition: "all 0.3s",
      background: hovered ? "#f0f7ff" : "transparent",
      boxShadow: hovered ? "0 2px 4px rgba(24, 144, 255, 0.1)" : "none",
    }),
  };

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    setConfig((prevConfig: Config | undefined) => {
      if (!prevConfig) {
        return config;
      }
      return {
        ...prevConfig,
        ...config,
      };
    });

    if (!save || !intentGroup) return;

    IntentService.setIntentGroupConfig(
      Number(intentGroupId),
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (updatedConfig: Config) => {
        setConfig(updatedConfig);
        message.success("Configuration updated successfully");
      },
      (error: any) => {
        console.error(error);
        message.error("Failed to update configuration");
      }
    );
  };

  const resetConfig = () => {
    if (!intentGroup) return;

    IntentService.resetIntentGroupConfig(
      Number(intentGroupId),
      null,
      apiKey
    ).subscribe(
      () => {
        setConfig(undefined);
        message.success("Configuration reset successfully");
      },
      (error: any) => {
        console.error(error);
        message.error("Failed to reset configuration");
      }
    );
  };

  const fetchRequests = (page: number) => {
    if (requestsByPageRef.current[page] && !startDate && !endDate) {
      setRequests(requestsByPageRef.current[page]);
      return;
    }

    const offset = (page - 1) * requestPageSize;
    setRequestsLoading(true);

    const start = startDate
      ? dayjs(startDate).startOf("day").utc().valueOf()
      : -1;
    const endTemp = endDate
      ? dayjs(endDate).add(1, "day").startOf("day").utc().valueOf()
      : initialLoadDate;
    const end = endTemp > initialLoadDate ? initialLoadDate : endTemp;

    IntentService.getIntentGroupRequests(
      Number(intentGroupId),
      start,
      end,
      offset,
      requestPageSize,
      setRequestsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setRequests(data.items || []);
        setRequestsTotal(data.total || 0);
        requestsByPageRef.current[page] = data.items || [];
        setRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setRequestsLoading(false);
        message.error("Failed to fetch requests");
      }
    );
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const fetchIntents = (page: number) => {
    if (intentsByPageRef.current[page]) {
      setIntents(intentsByPageRef.current[page]);
      return;
    }

    const offset = (page - 1) * 10;
    setIntentsLoading(true);

    IntentService.getIntentsByIntentGroup(
      Number(intentGroupId),
      offset,
      10,
      setIntentsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setIntents(data.items || []);
        setIntentsTotal(data.total || 0);
        intentsByPageRef.current[page] = data.items || [];
        setIntentsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIntentsLoading(false);
        message.error("Failed to fetch intents");
      }
    );
  };

  const handleDeleteTestSet = (testSet: TestSet) => {
    Modal.confirm({
      title: "Delete Test Set",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete test set "${testSet.name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        TestSetService.deleteTestSet(
          testSet.id.toString(),
          null,
          apiKey
        ).subscribe(
          () => {
            message.success({
              content: "Test set deleted successfully",
              style: { marginTop: "20px" },
            });
            const updatedTestSets = testSets.filter(
              (ts) => ts.id !== testSet.id
            );
            setTestSets(updatedTestSets);
            setCachedTestSets((prev) => ({
              ...prev,
              [testSetsCurrentPage]: updatedTestSets,
            }));
            setTestSetsTotal((prev) => prev - 1);
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: `Failed to delete test set: ${error}`,
            });
          }
        );
      },
    });
  };

  const fetchTestSets = (page: number) => {
    if (cachedTestSets[page]) {
      setTestSets(cachedTestSets[page]);
      return;
    }

    const offset = (page - 1) * testSetsPageSize;
    setTestSetsLoading(true);
    if (intentGroupId) {
      TestSetService.getTestSetsByIntentGroup(
        intentGroupId,
        offset,
        testSetsPageSize,
        setTestSetsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          setTestSets(response.items);
          setTestSetsTotal(response.total);
          setCachedTestSets((prev) => ({
            ...prev,
            [page]: response.items,
          }));
          setTestSetsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setTestSetsLoading(false);
        }
      );
    }
  };

  const fetchDatasets = (page: number) => {
    if (!intentGroupId) return;

    const offset = (page - 1) * datasetsPageSize;

    DatasetService.getDatasetsByIntentGroup(
      Number(intentGroupId),
      offset,
      datasetsPageSize,
      setDatasetsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setDatasets(response.items || []);
        setDatasetsTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching datasets:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch datasets: ${error}`,
        });
      }
    );
  };

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        true,
        setIsLoading,
        apiKey
      ).subscribe(
        (group: IntentGroup) => {
          setIntentGroup(group);
          setConfig(group.config);
          setOriginalConfig(group.config);
        },
        (error: any) => {
          console.error(error);
          message.error("Failed to load intent group");
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (intentGroupId && activeTab === "requests") {
      if (!requestsByPageRef.current[requestsCurrentPage]) {
        fetchRequests(requestsCurrentPage);
      } else {
        setRequests(requestsByPageRef.current[requestsCurrentPage]);
      }
    }
  }, [intentGroupId, requestsCurrentPage, activeTab]);

  useEffect(() => {
    if (
      (!startDate || startDate == null) &&
      (!endDate || endDate == null) &&
      activeTab === "requests"
    ) {
      requestsByPageRef.current = {};
      setRequestsCurrentPage(1);
      fetchRequests(1);
    }
  }, [startDate, endDate, activeTab]);

  useEffect(() => {
    if (intentGroupId && activeTab === "intents") {
      if (!intentsByPageRef.current[intentsCurrentPage]) {
        fetchIntents(intentsCurrentPage);
      } else {
        setIntents(intentsByPageRef.current[intentsCurrentPage]);
      }
    }
  }, [intentGroupId, intentsCurrentPage, activeTab]);

  useEffect(() => {
    if (intentGroupId && activeTab === "testsets") {
      fetchTestSets(testSetsCurrentPage);
    }
  }, [intentGroupId, testSetsCurrentPage, activeTab]);

  useEffect(() => {
    if (intentGroupId && activeTab === "datasets") {
      fetchDatasets(datasetsCurrentPage);
    }
  }, [intentGroupId, datasetsCurrentPage, activeTab]);

  const intentsColumns = [
    {
      title: "Application",
      dataIndex: ["meta", "metadata", "application_name"],
      key: "application_name",
      render: (text: string, record: any) => (
        <Link
          to={`/applications/${record.application_id}`}
          style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Link>
      ),
    },
    {
      title: "Intent",
      dataIndex: "action_type",
      key: "action_type",
      render: (text: string, record: any) => (
        <Link
          to={`/application/${record.application_id}/intent/${record.id}`}
          style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Link>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Last Activity",
      dataIndex: "last_activity",
      key: "last_activity",
      render: (timestamp: number) => (
        <Tooltip title={moment(timestamp).format("MMMM D, YYYY h:mm:ss A")}>
          {moment(timestamp).fromNow()}
        </Tooltip>
      ),
    },
    {
      title: "Requests",
      dataIndex: "request_count",
      key: "request_count",
      render: (count: number) => (
        <Typography.Text
          strong
          style={{ color: count > 0 ? "#52c41a" : "#8c8c8c" }}>
          {count}
        </Typography.Text>
      ),
    },
  ];

  const requestColumns: ColumnsType<ChatCompletionRequest> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "12.5%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Application",
      key: "application_name",
      width: "20%",
      render: (_, request: ChatCompletionRequest) => {
        const appName = (request.metadata?.application_name as string) || "-";
        const shortAppName = appName.length > 20
          ? `${appName.slice(0, 10)}...${appName.slice(-10)}`
          : appName;

        return (
          <Tooltip title={appName}>
            <Link
              to={`/application/${request.application_id}`}
              style={{ fontWeight: 500, color: "#1890ff" }}>
              {shortAppName}
            </Link>
          </Tooltip>
        );
      },
    },
    {
      title: "Model",
      key: "model",
      width: "12.5%",
      render: (_, request: ChatCompletionRequest) => {
        const model = request.params?.model || "";
        const shortModel = model.length > 10 
          ? `${model.slice(0, 5)}...${model.slice(-5)}`
          : model;
        
        return (
          <Tooltip title={model}>
            <Typography.Text>{shortModel}</Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Tags
          <AiStars style={styles.aiIconStyle} />
        </div>
      ),
      key: "tags",
      width: "35%",
      render: (_, request: ChatCompletionRequest, index: number) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          
          if (tags.length === 0) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }

          const visibleTags = tags.slice(0, 3);
          const remainingTags = tags.slice(3);
          
          return (
            <Space wrap size={4}>
              {visibleTags.map((tag: string) => (
                <Tag key={tag} color="blue" style={styles.tagStyle}>
                  {tag}
                </Tag>
              ))}
              {remainingTags.length > 0 && (
                <div className="tag-container" style={{ display: "inline-block", position: "relative" }}>
                  <Tag 
                    style={{ 
                      ...styles.tagStyle, 
                      cursor: "pointer",
                      position: "relative",
                      zIndex: 1
                    }}
                  >
                    +{remainingTags.length}
                  </Tag>
                  <div 
                    className="hidden-tags"
                    style={{ 
                      position: "absolute",
                      ...(index === 0 ? {
                        top: "calc(100% + 8px)",
                      } : {
                        bottom: "calc(100% + 8px)",
                      }),
                      left: "50%",
                      transform: `translateX(-50%) translateY(${index === 0 ? "0" : "-10px"})`,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      opacity: 0,
                      transition: "all 0.2s ease-in-out",
                      pointerEvents: "none",
                      zIndex: 2,
                      maxWidth: "300px",
                      border: "1px solid rgba(0, 0, 0, 0.06)"
                    }}
                  >
                    {remainingTags.map((tag: string) => (
                      <Tag key={tag} color="blue" style={styles.tagStyle}>
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </Space>
          );
        } catch (e) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "15%",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        const shortId = `...${requestId.slice(-8)}`;

        return (
          <Space>
            <Typography.Text style={styles.monospaceFont} copyable={false}>
              {shortId}
            </Typography.Text>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success({
                  content: "Request ID copied to clipboard",
                  icon: <CopyOutlined style={{ color: "#52c41a" }} />,
                });
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "5%",
      align: "center",
      render: (_, record: ChatCompletionRequest) => (
        <RequestViewButton
          apiKey={apiKey}
          request={record}
          to={`/application/${record.application_id}/session/${encodeURIComponent(record.session_id)}/request/${record.request_id}`}
          showIcon={false}
        >
          <InfoCircleOutlined 
            style={{ 
              color: "#1890ff",
              fontSize: "16px",
              transition: "all 0.3s",
            }}
          />
        </RequestViewButton>
      ),
    },
  ];

  const testSetColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
      render: (text: string) => (
        <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: false,
      width: "30%",
      render: (text: string | undefined) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            color: text ? "#262626" : "#8c8c8c",
          }}>
          {text || "No description provided"}
        </div>
      ),
    },
    {
      title: (
        <Space>
          Golden
          <Tooltip title="Test sets marked as 'Golden' will be used to evaluate the performance of models after fine-tuning">
            <InfoCircleOutlined
              style={{
                color: "rgba(0, 0, 0, 0.45)",
                fontSize: "14px",
              }}
            />
          </Tooltip>
        </Space>
      ),
      dataIndex: "is_golden",
      key: "is_golden",
      width: "15%",
      render: (isGolden: boolean) => (
        <Typography.Text
          style={{
            color: isGolden ? "#52c41a" : "#8c8c8c",
            fontWeight: isGolden ? 500 : 400,
          }}>
          {isGolden ? "Yes" : "No"}
        </Typography.Text>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (text: number) => {
        const date = moment(text);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
      sorter: (a: TestSet, b: TestSet) =>
        moment(a.date_created).unix() - moment(b.date_created).unix(),
    },
    {
      title: "# Requests",
      dataIndex: ["meta", "request_count"],
      key: "request_count",
      width: "15%",
      render: (count: number) => (
        <Typography.Text
          strong
          style={{ color: count > 0 ? "#52c41a" : "#8c8c8c" }}>
          {count}
        </Typography.Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: "48px",
      render: (_: any, record: TestSet) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDeleteTestSet(record);
                },
              },
            ],
          }}
          trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  // Add global CSS for interactive rows
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .details-arrow {
        cursor: pointer;
        transform: rotate(-45deg);
      }
      .details-arrow:hover {
        transform: rotate(-45deg) scale(1.2);
      }
      .interactive-row {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .tag-container:hover .hidden-tags {
        opacity: 1 !important;
        transform: translateY(0) !important;
        pointer-events: auto !important;
      }
      .hidden-tags {
        background: rgba(255, 255, 255, 0.9) !important;
        backdrop-filter: blur(8px) !important;
        -webkit-backdrop-filter: blur(8px) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  // Parse tags from metadata
  const getTags = () => {
    try {
      return JSON.parse((intentGroup?.metadata?.tags as string) || "[]");
    } catch (e) {
      return [];
    }
  };

  // Parse description from metadata
  const getDescription = () => {
    try {
      return (intentGroup?.metadata?.intent as string) || "-";
    } catch (e) {
      return "-";
    }
  };

  return (
    <div
      className="intent-group-overview-container"
      style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/intent-groups">Intent Groups</Link> /{" "}
                {intentGroup?.intent_name}
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                {intentGroup?.intent_name}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage intent configuration, test sets, and monitor performance
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}>
        {/* Overview Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <AppstoreOutlined style={styles.tabIcon} />
              Overview
            </span>
          }
          key="overview">
          <div style={styles.contentCard}>
            <Row gutter={[24, 24]}>
              {/* Stats Row */}
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <div style={styles.statsCard("#1890ff")}>
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Total Requests
                          </Typography.Text>
                        }
                        value={intentGroup?.request_count || 0}
                        prefix={
                          <FileTextOutlined
                            style={{
                              color: "#1890ff",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={styles.statsCard("#52c41a")}>
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Last Activity
                          </Typography.Text>
                        }
                        value={
                          intentGroup?.last_activity
                            ? moment(intentGroup?.last_activity).fromNow()
                            : "Never"
                        }
                        prefix={
                          <HistoryOutlined
                            style={{
                              color: "#52c41a",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={styles.statsCard("#722ed1")}>
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Notifications
                          </Typography.Text>
                        }
                        value={
                          intentGroup?.metadata?.notifications_enabled
                            ? "Enabled"
                            : "Disabled"
                        }
                        prefix={
                          <BellOutlined
                            style={{
                              color: "#722ed1",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>

              {/* Details Section */}
              <Col span={24}>
                <div style={styles.detailsContainer}>
                  <div style={styles.detailsHeader}>
                    <Typography.Title
                      level={5}
                      style={{ margin: 0, fontWeight: 600 }}>
                      Details
                    </Typography.Title>
                  </div>
                  <div style={styles.detailsContent}>
                    <Row gutter={[0, 24]}>
                      <Col span={24}>
                        <Typography.Text style={styles.subtitle}>
                          Description
                          <AiStars style={styles.aiIconStyle} />
                        </Typography.Text>
                        <Typography.Paragraph
                          style={{
                            whiteSpace: "pre-wrap",
                            margin: 0,
                            fontSize: "14px",
                            lineHeight: "1.6",
                          }}>
                          {getDescription()}
                        </Typography.Paragraph>
                      </Col>
                      <Col span={24}>
                        <Typography.Text style={styles.subtitle}>
                          Tags
                          <AiStars style={styles.aiIconStyle} />
                        </Typography.Text>
                        <div>
                          <Space wrap size={8}>
                            {getTags().map((tag: string) => (
                              <Tag
                                key={tag}
                                color="blue"
                                style={styles.tagStyle}>
                                {tag}
                              </Tag>
                            ))}
                            {getTags().length === 0 && (
                              <Typography.Text type="secondary">
                                No tags available
                              </Typography.Text>
                            )}
                          </Space>
                        </div>
                      </Col>
                      <Col span={24}>
                        <Typography.Text style={styles.subtitle}>
                          Date Created
                        </Typography.Text>
                        <Typography.Paragraph
                          style={{ margin: 0, fontSize: "14px" }}>
                          {intentGroup?.date_created
                            ? moment(intentGroup.date_created).format(
                                "MMMM D, YYYY"
                              )
                            : "-"}
                        </Typography.Paragraph>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* Intents Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileTextOutlined style={styles.tabIcon} />
              Intents
            </span>
          }
          key="intents">
          <div style={styles.contentCard}>
            <div style={{ marginBottom: "24px" }}>
              <Input.Search
                placeholder="Search intents"
                style={{ width: 300 }}
                allowClear
              />
            </div>
            <IntentsTable
              intentGroupId={Number(intentGroupId)}
              showApplicationColumn={true}
              styles={styles}
              emptyText={
                <Empty
                  description={
                    <div>
                      <Typography.Text
                        strong
                        style={{ display: "block", marginBottom: "8px" }}>
                        No intents found
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        No intents have been created in this group yet.
                      </Typography.Text>
                    </div>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              }
            />
          </div>
        </TabPane>

        {/* Requests Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <SearchOutlined style={styles.tabIcon} />
              Requests
            </span>
          }
          key="requests">
          <div style={styles.contentCard}>
            <div style={{ marginBottom: "24px" }}>
              <Row gutter={[16, 16]} align="middle">
                <Col>
                  <DatePicker
                    placeholder="Start Date"
                    onChange={(date: Dayjs | null) => setStartDate(date)}
                    value={startDate}
                    disabledDate={(current: Dayjs) =>
                      endDate ? current > endDate : false
                    }
                    style={{ width: "160px" }}
                    format="YYYY-MM-DD"
                    allowClear
                  />
                </Col>
                <Col>
                  <DatePicker
                    placeholder="End Date"
                    onChange={(date: Dayjs | null) => setEndDate(date)}
                    value={endDate}
                    disabledDate={(current: Dayjs) =>
                      startDate ? current < startDate : false
                    }
                    style={{ width: "160px" }}
                    format="YYYY-MM-DD"
                    allowClear
                  />
                </Col>
                <Col>
                  <Button
                    onClick={handleClearFilters}
                    disabled={!endDate && !startDate}>
                    Clear
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => {
                      requestsByPageRef.current = {};
                      setRequestsCurrentPage(1);
                      fetchRequests(1);
                    }}
                    disabled={!startDate && !endDate}>
                    Search
                  </Button>
                </Col>
              </Row>
            </div>

            <div style={styles.tableContainer}>
              <Table
                rowKey="request_id"
                columns={requestColumns}
                dataSource={requests}
                loading={requestsLoading}
                rowClassName={() => "interactive-row"}
                pagination={{
                  current: requestsCurrentPage,
                  pageSize: requestPageSize,
                  total: requestsTotal,
                  onChange: (page, pageSize) => {
                    setRequestsCurrentPage(page);
                    if (pageSize && pageSize !== requestPageSize) {
                      setRequestPageSize(pageSize);
                      requestsByPageRef.current = {};
                    }
                  },
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onShowSizeChange: (current, size) => {
                    setRequestsCurrentPage(1);
                    setRequestPageSize(size);
                    requestsByPageRef.current = {};
                  },
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description={
                        <div>
                          <Typography.Text
                            strong
                            style={{ display: "block", marginBottom: "8px" }}>
                            No requests found
                          </Typography.Text>
                          <Typography.Text type="secondary">
                            Try adjusting your date filters or check back later.
                          </Typography.Text>
                        </div>
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
              />
            </div>
          </div>
        </TabPane>

        {/* Sentinels Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <SafetyOutlined style={styles.tabIcon} />
              Sentinels
            </span>
          }
          key="sentinels">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}>
              <Typography.Title
                level={5}
                style={{ margin: 0, fontWeight: 600 }}>
                Sentinels
              </Typography.Title>
              <PrimaryButton
                title="New Sentinel"
                icon={<PlusOutlined />}
                onClick={() =>
                  navigate(`/intent-group/${intentGroupId}/sentinel/new`)
                }
              />
            </div>
            <SentinelList
              intentGroupId={Number(intentGroupId)}
              emptyText={
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      <Typography.Text
                        strong
                        style={{ display: "block", marginBottom: "8px" }}>
                        No sentinels found
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        Create a new sentinel to monitor and evaluate intents.
                      </Typography.Text>
                    </div>
                  }
                />
              }
              excludeHeader={true}
            />
          </div>
        </TabPane>

        {/* Models Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <ApiOutlined style={styles.tabIcon} />
              Models
            </span>
          }
          key="models">
          <div style={styles.contentCard}>
            <ModelTable apiKey={apiKey} intentGroupId={Number(intentGroupId)} />
          </div>
        </TabPane>

        {/* Configuration Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <SettingOutlined style={styles.tabIcon} />
              Configuration
            </span>
          }
          key="configuration">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "16px",
              }}>
              <OutlineButton icon={<ReloadOutlined />} onClick={resetConfig}>
                Reset to Default
              </OutlineButton>
            </div>
            <ConfigurationPanel
              intentGroupId={Number(intentGroupId)}
              config={config}
              updateConfig={updateConfig}
              resetConfig={resetConfig}
              showSystemPrompt={false}
            />
          </div>
        </TabPane>

        {/* Test Sets Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileTextOutlined style={styles.tabIcon} />
              Test Sets
            </span>
          }
          key="testsets">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}>
              <Typography.Title
                level={5}
                style={{ margin: 0, fontWeight: 600 }}>
                Test Sets
              </Typography.Title>
              <PrimaryButton
                title="New Test Set"
                icon={<PlusOutlined />}
                onClick={() =>
                  navigate(`/intent-group/${intentGroupId}/testsets/new`)
                }
              />
            </div>
            <Table
              rowKey={(record) => record.id.toString()}
              columns={testSetColumns}
              dataSource={testSets}
              loading={testSetsLoading}
              rowClassName={() => "interactive-row"}
              pagination={{
                current: testSetsCurrentPage,
                pageSize: testSetsPageSize,
                total: testSetsTotal,
                showSizeChanger: false,
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={(pagination) => {
                if (pagination.current) {
                  setTestSetsCurrentPage(pagination.current);
                }
              }}
              onRow={(record) => {
                return {
                  onClick: () =>
                    navigate(
                      `/intent-group/${intentGroupId}/testsets/${record.id}`
                    ),
                  style: { cursor: "pointer" },
                };
              }}
              locale={{
                emptyText: (
                  <Empty
                    description={
                      <div>
                        <Typography.Text
                          strong
                          style={{ display: "block", marginBottom: "8px" }}>
                          No test sets found
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          Create a new test set to evaluate your model's
                          performance.
                        </Typography.Text>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </div>
        </TabPane>

        {/* Datasets Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileTextOutlined style={styles.tabIcon} />
              Datasets
            </span>
          }
          key="datasets">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}>
              <Typography.Title
                level={5}
                style={{ margin: 0, fontWeight: 600 }}>
                Datasets
              </Typography.Title>
              <PrimaryButton
                title="New Dataset"
                icon={<PlusOutlined />}
                onClick={() =>
                  navigate(
                    `/models/datasets/new?intent_group_id=${intentGroupId}`
                  )
                }
              />
            </div>
            <DatasetsTable
              datasets={datasets}
              loading={datasetsLoading}
              total={datasetsTotal}
              currentPage={datasetsCurrentPage}
              pageSize={datasetsPageSize}
              onPageChange={setDatasetsCurrentPage}
              onRefreshData={fetchDatasets}
              setLoading={setDatasetsLoading}
            />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default IntentGroupOverview;
